import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PackageCard from "@/components/site/PackageCard";
import { useCatalog } from "@/lib/CatalogProvider";
import SectionGlow from "@/components/site/SectionGlow";
import { cn } from "@/lib/utils";

const PROCESS = [
    {
        no: "01 · Direction",
        title: "Strategy & Direction",
        body: "We define positioning, tone, and the story your brand needs to own before a single mark is drawn.",
    },
    {
        no: "02 · Identity",
        title: "Identity & System",
        body: "We design the logo system, type, palette, and the full visual language that makes you unmistakable.",
    },
    {
        no: "03 · Rollout",
        title: "Delivery & Rollout",
        body: "We hand over a production-ready brand kit with guidelines, ready to apply everywhere with confidence.",
    },
];

export const Services = ({ onChoosePackage }) => {
    const { packages } = useCatalog();
    const reduce = useReducedMotion();
    const [activeStep, setActiveStep] = useState(0);

    // Cycle the "active stage" light 01 -> 02 -> 03 -> loop, so the process
    // reads as a journey the client moves through.
    useEffect(() => {
        if (reduce) return;
        const id = setInterval(() => setActiveStep((s) => (s + 1) % PROCESS.length), 1900);
        return () => clearInterval(id);
    }, [reduce]);

    const logoPkgs = (packages || [])
        .filter((p) => p.kind === "logo" && p.visible !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    const brandingPkgs = (packages || [])
        .filter((p) => p.kind === "branding" && p.visible !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

    return (
        <section id="services" className="pg-section" aria-label="Services" data-testid="services-section">
            <SectionGlow tone="warm" />
            <div className="pg-container">
                {/* Section header — story before pricing */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="max-w-3xl"
                >
                    <h2
                        className="pg-h2-section"
                        style={{ color: "var(--pg-fg)" }}
                    >
                        Great brands are built in
                        {" "}<span className="pg-text-gold-static">deliberate steps</span>.
                    </h2>
                    <p className="pg-lede mt-5 max-w-xl">
                        Before the price, the process. Every engagement follows the same disciplined
                        path, so you always know how a focused logo or a full brand ecosystem comes together.
                    </p>
                </motion.div>

                {/* Process visualization — a live "client journey" stepper */}
                <div className="mt-12" data-testid="services-process">
                    <div className="pg-stepper" aria-hidden="true">
                        <span className="pg-stepper__track">
                            <span
                                className="pg-stepper__fill"
                                style={{ "--p": `${(activeStep / (PROCESS.length - 1)) * 100}%` }}
                            />
                        </span>
                        {PROCESS.map((_, i) => (
                            <span key={i} className={cn("pg-stepper__node", activeStep >= i && "is-on")}>
                                {i + 1}
                            </span>
                        ))}
                    </div>

                    <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
                        {PROCESS.map((step, idx) => (
                            <motion.div
                                key={step.no}
                                initial={{ opacity: 0, y: 22 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.6, delay: 0.18 * idx, ease: [0.16, 1, 0.3, 1] }}
                                className={cn("pg-step p-5 sm:p-6", activeStep === idx && "is-active")}
                                data-active={activeStep === idx ? "true" : "false"}
                            >
                                <span className="pg-step__ring" aria-hidden="true" />
                                <div className="flex items-center gap-3">
                                    <span className="pg-step__badge">{idx + 1}</span>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-accent)" }}>
                                        {step.no}
                                    </span>
                                </div>
                                <h3 className="font-display mt-3 text-lg sm:text-xl" style={{ color: "var(--pg-fg)" }}>
                                    {step.title}
                                </h3>
                                <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "var(--pg-fg-muted)" }}>
                                    {step.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Packages lead-in */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-20 max-w-2xl"
                >
                    <h3 className="font-display text-[clamp(1.5rem,3vw,2.2rem)] leading-[1.1]" style={{ color: "var(--pg-fg)" }}>
                        Choose the level of <span className="pg-em-italic">ambition</span>.
                    </h3>
                    <p className="pg-lede mt-4">
                        Start with a focused logo system or commission a full brand ecosystem. One flagship
                        tier is built as a complete, cinematic identity engagement.
                    </p>
                </motion.div>

                {/* Logo Packages */}
                {logoPkgs.length > 0 && (
                    <div className="mt-8" id="logo-packages" data-testid="services-logo-section">
                        <div className="flex flex-wrap items-end justify-between gap-4">
                            <h3
                                className="font-display text-[clamp(1.6rem,2.8vw,2.4rem)]"
                                style={{ color: "var(--pg-fg)" }}
                            >
                                Logo Design Packages
                            </h3>
                            <span className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                                {logoPkgs.length} {logoPkgs.length === 1 ? "tier" : "tiers"}
                            </span>
                        </div>
                        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                            {logoPkgs.map((pkg, idx) => (
                                <PackageCard
                                    key={pkg.id}
                                    pkg={pkg}
                                    index={idx}
                                    variant="logo"
                                    onChoose={onChoosePackage}
                                    testIdPrefix="logo-package"
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Branding Packages */}
                {brandingPkgs.length > 0 && (
                    <div className="mt-20" id="branding-packages" data-testid="services-branding-section">
                        <div className="flex flex-wrap items-end justify-between gap-4">
                            <h3
                                className="font-display text-[clamp(1.6rem,2.8vw,2.4rem)]"
                                style={{ color: "var(--pg-fg)" }}
                            >
                                Full Branding Packages
                            </h3>
                            <span className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                                {brandingPkgs.length} {brandingPkgs.length === 1 ? "tier" : "tiers"}
                            </span>
                        </div>
                        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                            {brandingPkgs.map((pkg, idx) => (
                                <PackageCard
                                    key={pkg.id}
                                    pkg={pkg}
                                    index={idx}
                                    variant="branding"
                                    onChoose={onChoosePackage}
                                    testIdPrefix="branding-package"
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Custom note */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mt-12 rounded-2xl border p-6 text-center sm:p-8"
                    style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                >
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                        Need something more bespoke?
                    </p>
                    <p className="mt-3 text-base sm:text-lg" style={{ color: "var(--pg-fg)" }}>
                        Tell us about your brand and we’ll craft a custom engagement, strategy, identity, motion,
                        and beyond.
                    </p>
                    <Link to="/contact" className="pg-btn-ghost mt-6 inline-flex" data-testid="services-custom-cta">
                        Start a conversation
                        <ArrowUpRight className="h-4 w-4" strokeWidth={1.6} />
                    </Link>
                </motion.div>
            </div>
        </section>
    );
};

export default Services;
